import type { Href } from "expo-router";
import { useFocusEffect, usePathname, useRouter } from "expo-router";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Platform, Pressable, Text, useWindowDimensions, View, type ViewStyle } from "react-native";
import { COLORS, FONTS, RADII, SPACING, STROKES } from "../../lib/brand";
import { LogoMark, LogoWordmark } from "../brand/Logo";
import { PANE_THEMES } from "../brand/paneThemes";
import Svg, { Circle, Path, Rect } from "react-native-svg";
import { SafeAreaView } from "react-native-safe-area-context";

import { Avatar } from "../ui/Avatar";
import { useAuth } from "../../lib/authContext";
import { signOutToHome } from "../../lib/auth";
import { getUnreadCount } from "../../lib/notifications";
import {
  StudentHeaderOverlays,
  type StudentOverlayKey,
} from "./HeaderOverlays";
import { BrandBackdrop } from "./BrandBackdrop";

const SIDEBAR_W = 252;
// Idle topbar icon tint — cream at low opacity since the topbar is now COLORS.FOREST.hex.
// (Previously a muted forest grey, when the topbar was cream.)

const STUDENT_THEME = PANE_THEMES.Student;

export type StudentPortalIdentity = {
  fullName: string;
  subtitleLine: string;
};

type NavKey =
  | "dashboard"
  | "cases"
  | "teams"
  | "submissions"
  | "activity"
  | "profile"
  | "workspace"
  | "auxiliary";

function navKeyFromPath(path: string): NavKey {
  const normalized = (path.split("?")[0] ?? "").replace(/\/+$/, "") || "/";
  if (normalized.includes("/student/competition")) return "workspace";
  if (normalized.endsWith("/student/profile")) return "profile";
  if (normalized.endsWith("/student/cases")) return "cases";
  if (normalized.endsWith("/student/teams")) return "teams";
  if (normalized.endsWith("/student/submissions")) return "submissions";
  if (
    normalized === "/notifications" ||
    normalized === "/student/notifications" ||
    normalized.startsWith("/announcements")
  )
    return "activity";
  if (normalized === "/student") return "dashboard";
  return "auxiliary";
}

function IconDashboard({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Rect x={1} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={8} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={1} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={8} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
    </Svg>
  );
}

function IconCases({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M7 1l1.5 4h4.5l-3.5 2.5 1.5 4L7 9l-4 2.5 1.5-4L1 5h4.5z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function IconTeams({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={5} cy={4} r={2.2} stroke={color} strokeWidth={1.1} />
      <Circle cx={10} cy={5} r={1.8} stroke={color} strokeWidth={1.1} />
      <Path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      <Path d="M10 8c1.7 0 3 1.1 3 2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconSubmissions({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path d="M12 8v3a1 1 0 01-1 1H3a1 1 0 01-1-1V8" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      <Path d="M7 1v7M4.5 3.5L7 1l2.5 2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function IconProfile({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={5} r={3} stroke={color} strokeWidth={1.1} />
      <Path d="M2 12c0-2.761 2.239-5 5-5s5 2.239 5 5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}
function IconActivity({ color }: { color: string }) {
  // Bell-shaped sidebar icon to match the topbar bell.
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M3.6 4.8a3.4 3.4 0 0 1 6.8 0c0 3.2 1 4.2 1.5 4.6H2.1c0.5-0.4 1.5-1.4 1.5-4.6Z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <Path d="M5.95 11.5a1.1 1.1 0 0 0 2.1 0" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}


/** Envelope icon — opens the Activity overlay. */
/** Search glyph — opens the global Cmd/Ctrl+K command palette. */
function IconHeaderSearch({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <Circle cx={8} cy={8} r={5.5} stroke={color} strokeWidth={1.4} />
      <Path
        d="M12.5 12.5 16 16"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Bell glyph — opens the Activity overlay. Replaces the older envelope
 *  icon to better signal "notifications" rather than mail/messages. */
function IconHeaderEnvelope({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <Path
        d="M4.2 7.5a4.8 4.8 0 0 1 9.6 0c0 4.4 1.4 5.8 1.9 6.4H2.3c0.5-0.6 1.9-2 1.9-6.4Z"
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin="round"
        fill="none"
      />
      <Path
        d="M7.4 14.6a1.6 1.6 0 0 0 3.2 0"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
        fill="none"
      />
    </Svg>
  );
}

/** Gear icon — opens the Settings overlay. (Lucide settings glyph.) */
function IconHeaderGear({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
      <Path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Circle cx={12} cy={12} r={3} stroke={color} strokeWidth={1.5} />
    </Svg>
  );
}

/**
 * Header icon button — used by the persistent student header for the
 * Activity / Settings / Profile overlays. Calls `onPress` instead of
 * navigating, since the overlays don't change the route. Renders an
 * optional dot badge in the top-right (used for Activity unread).
 */
function HeaderIconButton({
  onPress,
  accessibilityLabel,
  active,
  children,
  badgeCount,
}: {
  onPress: () => void;
  accessibilityLabel: string;
  active: boolean;
  children: (iconColor: string) => ReactNode;
  badgeCount?: number | null;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  // Idle icons sit at a muted grey-forest so the topbar reads as quiet
  // chrome rather than four high-contrast "buttons". Hover / active still
  // pull all the way to amber so they pop when the user is interacting.
  const iconColor = active || (web && hover) ? COLORS.AMBER.hex : STUDENT_THEME.textOnSidebarDim;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      {...(web ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const) : {})}
      style={[
        {
          width: 34,
          height: 34,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: RADII.xs,
          backgroundColor: active ? STUDENT_THEME.accentTintSoft : "transparent",
        },
      ]}
    >
      {children(iconColor)}
      {badgeCount != null && badgeCount > 0 ? (
        <View
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            minWidth: 14,
            height: 14,
            paddingHorizontal: 3,
            borderRadius: 7,
            backgroundColor: COLORS.AMBER.hex,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 8.5, color: COLORS.FOREST.hex }}>
            {badgeCount > 99 ? "99+" : String(badgeCount)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

function SidebarNavItem({
  icon,
  label,
  active,
  onPress,
  badgeCount,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onPress: () => void;
  badgeCount?: number | null;
}) {
  // `transition` is a CSS-on-web extension RN's ViewStyle doesn't model;
  // cast through unknown to keep the runtime CSS without a type error.
  const webHover =
    Platform.OS === "web"
      ? ({ transition: "background-color 0.15s ease" } as unknown as ViewStyle)
      : {};
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed, hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 9,
          paddingVertical: SPACING.xs,
          paddingHorizontal: 20,
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? COLORS.AMBER.hex : "transparent",
          backgroundColor: active ? STUDENT_THEME.accentTintSoft : pressed ? STUDENT_THEME.neutralHoverBgOnDark : "transparent",
        },
        Platform.OS === "web" && hovered && !active ? { backgroundColor: STUDENT_THEME.neutralHoverBgOnDark } : null,
        webHover,
      ]}
    >
      <View style={{ opacity: active ? 1 : 0.55 }}>{icon}</View>
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.2,
          color: active ? COLORS.LINEN.hex : STUDENT_THEME.textOnSidebarIdle,
        }}
      >
        {label}
      </Text>
      {badgeCount != null ? (
        <View className="rounded-md px-1.5 py-0.5" style={{ backgroundColor: COLORS.AMBER.hex }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, color: COLORS.FOREST.hex }}>
            {badgeCount > 99 ? "99+" : String(badgeCount)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

export type StudentPortalShellProps = {
  children: ReactNode;
  topbarTitle: string;
  topbarFirstName: string;
  avatarName: string;
  avatarUrl?: string | null;
  identity?: StudentPortalIdentity | null;
  casesBadgeCount?: number;
  /** Unread team-chat messages across every team the student is on — drives the Teams nav badge. */
  teamsUnreadCount?: number;
  /** e.g. notifications “Mark all as read” — rendered in the top bar before utility icons */
  topbarRightAccessory?: ReactNode;
  /**
   * Hide the green sidebar — used by surfaces that don't want any
   * portal-level navigation (e.g. the v2 student top-level dashboard,
   * which puts the Find Competition button + program cards front and
   * center). Defaults to false; existing pages keep the sidebar.
   */
  hideSidebar?: boolean;
};

export function StudentPortalShell({
  children,
  topbarTitle,
  topbarFirstName,
  avatarName,
  avatarUrl = null,
  identity = null,
  casesBadgeCount = 0,
  teamsUnreadCount = 0,
  topbarRightAccessory = null,
  hideSidebar = false,
}: StudentPortalShellProps) {
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const showSidebar = !hideSidebar && width >= 900;
  const active = navKeyFromPath(pathname);

  // Two-click sign-out confirmation. First click swaps the label to a
  // confirm prompt; second click within 4s actually signs out. Resets if
  // the user clicks anything else.
  const [signOutConfirm, setSignOutConfirm] = useState(false);

  // Persistent header overlays (Activity / Settings / Profile). Opening
  // an overlay does NOT navigate — the page stays underneath, blurred.
  const [openOverlay, setOpenOverlay] = useState<StudentOverlayKey>(null);

  // Sidebar Activity badge — refreshes whenever any student page regains focus.
  const [activityUnread, setActivityUnread] = useState(0);
  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      void getUnreadCount().then((n) => {
        if (!cancelled) setActivityUnread(n);
      });
      return () => {
        cancelled = true;
      };
    }, [])
  );

  // Close any open overlay; if it was the Activity overlay, refresh the
  // badge in case the user marked things read inside it.
  const closeOverlay = useCallback(() => {
    setOpenOverlay((prev) => {
      if (prev === "activity") {
        void getUnreadCount().then(setActivityUnread);
      }
      return null;
    });
  }, []);

  // Cmd/Ctrl+K opens the global search palette from anywhere in the
  // student portal (web only). The handler is mounted at shell level so
  // the binding is live regardless of which page is in view.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpenOverlay((prev) => (prev === "search" ? null : "search"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const displayName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "Student";

  const resolvedIdentity: StudentPortalIdentity = identity ?? {
    fullName: displayName,
    subtitleLine: "Student",
  };

  const iconColor = (key: NavKey) => (active === key ? COLORS.AMBER.hex : COLORS.SAGE.hex);

  return (
    <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
      <View className="flex-1 flex-row">
        {showSidebar ? (
          <View
            style={{
              width: SIDEBAR_W,
              backgroundColor: COLORS.FOREST.hex,
              borderRightWidth: STROKES.hairline,
              borderRightColor: STUDENT_THEME.accentTintSoft,
            }}
          >
            <Pressable
              onPress={() => router.push("/student" as Href)}
              className="flex-row items-center gap-2.5 border-b px-5"
              style={{
                height: 56,
                borderBottomColor: STUDENT_THEME.accentTintSoft,
              }}
            >
              <View className="h-8 w-8 items-center justify-center rounded-md bg-forest-mid">
                <LogoMark
                  size={20}
                  tile={COLORS.FOREST_MID.hex}
                  ringOuter={COLORS.SAGE.hex}
                  ringInner={COLORS.AMBER.hex}
                  dot={COLORS.AMBER.hex}
                  rays="none"
                />
              </View>
              <LogoWordmark
                className="text-[17.6px] font-semibold tracking-widest-2 text-linen"
                accent={COLORS.AMBER.hex}
              />
            </Pressable>

            <View className="flex-1 py-3.5">
              <Text
                className="mb-1 px-5 text-[8.5px] font-bold uppercase tracking-widest-3 text-linen/25"
                style={{ fontFamily: FONTS.bodyBold }}
              >
                Student Portal
              </Text>
              <SidebarNavItem
                icon={<IconDashboard color={iconColor("dashboard")} />}
                label="Dashboard"
                active={active === "dashboard"}
                onPress={() => router.push("/student" as Href)}
              />
              <SidebarNavItem
                icon={<IconCases color={iconColor("cases")} />}
                label="Cases"
                active={active === "cases"}
                onPress={() => router.push("/student/cases" as Href)}
                badgeCount={casesBadgeCount}
              />
              <SidebarNavItem
                icon={<IconTeams color={iconColor("teams")} />}
                label="Teams"
                active={active === "teams"}
                onPress={() => router.push("/student/teams" as Href)}
                badgeCount={teamsUnreadCount > 0 ? teamsUnreadCount : null}
              />
              <SidebarNavItem
                icon={<IconSubmissions color={iconColor("submissions")} />}
                label="My Submissions"
                active={active === "submissions"}
                onPress={() => router.push("/student/submissions" as Href)}
              />
              <SidebarNavItem
                icon={<IconActivity color={iconColor("activity")} />}
                label="Activity"
                active={active === "activity"}
                onPress={() => router.push("/student/notifications" as Href)}
                badgeCount={activityUnread > 0 ? activityUnread : null}
              />
                            <SidebarNavItem
                icon={<IconProfile color={iconColor("profile")} />}
                label="My Profile"
                active={active === "profile"}
                onPress={() => router.push("/student/profile" as Href)}
              />
            </View>

            <View className="border-t px-5 py-3.5" style={{ borderTopColor: STUDENT_THEME.accentTintSoft }}>
              <Text className="text-[12px] font-semibold text-linen" style={{ fontFamily: FONTS.bodySemiBold }}>
                {resolvedIdentity.fullName}
              </Text>
              <Text className="mt-0.5 text-[11px] leading-4 text-linen/35" style={{ fontFamily: FONTS.body }}>
                {resolvedIdentity.subtitleLine}
              </Text>
              <Pressable
                onPress={() => {
                  if (signOutConfirm) {
                    void signOutToHome(router);
                  } else {
                    setSignOutConfirm(true);
                    // Auto-cancel the confirm prompt after 4s if not clicked again.
                    setTimeout(() => setSignOutConfirm(false), 4000);
                  }
                }}
                className="mt-2 flex-row items-center gap-1"
              >
                <Text
                  className="text-[11px]"
                  style={{
                    fontFamily: signOutConfirm ? FONTS.bodyBold : FONTS.bodyMedium,
                    color: signOutConfirm ? COLORS.AMBER.hex : STUDENT_THEME.textOnSidebarFaint,
                  }}
                >
                  {signOutConfirm ? "Click again to confirm sign out" : "Sign out"}
                </Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        <View className="min-w-0 flex-1">
          <View
            className="h-14 flex-row items-center justify-between px-5"
            style={{
              backgroundColor: STUDENT_THEME.topbarBg,
              borderBottomWidth: STROKES.hairline,
              borderBottomColor: STUDENT_THEME.surfaceDividerFaint,
              ...(Platform.OS === "web"
                ? ({
                    backdropFilter: "blur(12px)",
                  } as object)
                : {}),
            }}
          >
            <View className="min-w-0 flex-1 flex-row items-center pr-3" style={{ gap: 12 }}>
              {/* Coravu mark — forest-green tile with the targeting-circle
                  glyph and the wordmark beside it. Click returns to the
                  student dashboard. */}
              <Pressable
                onPress={() => router.push("/student" as Href)}
                accessibilityRole="link"
                accessibilityLabel="Coravu home"
                style={({ hovered }) => [
                  {
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 9,
                    paddingHorizontal: SPACING.xs,
                    paddingVertical: 5,
                    borderRadius: RADII.sm,
                  },
                  Platform.OS === "web" && hovered
                    ? ({ backgroundColor: STUDENT_THEME.surfaceDividerFaint } as object)
                    : null,
                  Platform.OS === "web"
                    ? ({ transition: "background-color 0.18s ease" } as const)
                    : null,
                ]}
              >
                <View
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 7,
                    backgroundColor: COLORS.FOREST_MID.hex,
                    alignItems: "center",
                    justifyContent: "center",
                    shadowColor: "#000",
                    shadowOpacity: 0.22,
                    shadowRadius: 8,
                    shadowOffset: { width: 0, height: 3 },
                  }}
                >
                  <LogoMark
                    size={20}
                    tile={COLORS.FOREST_MID.hex}
                    ringOuter={COLORS.SAGE.hex}
                    ringInner={COLORS.AMBER.hex}
                    dot={COLORS.AMBER.hex}
                    rays="none"
                  />
                </View>
                <LogoWordmark
                  fontSize={16}
                  letterSpacing={2.4}
                  color={COLORS.LINEN.hex}
                  accent={COLORS.AMBER.hex}
                />
              </Pressable>

              {topbarTitle ? (
                <>
                  {/* Vertical divider between logo and page title, tinted
                      cream so it reads softly against the forest topbar. */}
                  <View
                    style={{
                      width: 1,
                      height: 22,
                      backgroundColor: STUDENT_THEME.surfaceDividerStrong,
                      marginHorizontal: 4,
                    }}
                  />
                  <Text
                    className="min-w-0 flex-1 text-lg"
                    style={{ fontFamily: FONTS.displayMedium, color: COLORS.LINEN.hex }}
                    numberOfLines={1}
                  >
                    {topbarTitle}
                  </Text>
                </>
              ) : null}
            </View>
            <View className="max-w-[55%] flex-row shrink-0 items-center gap-2">
              {topbarRightAccessory ? (
                <View className="mr-1 max-w-[38%] shrink">{topbarRightAccessory}</View>
              ) : null}
              {/*
                Persistent header icons (Activity / Settings / Profile).
                Each opens a modal-style overlay — the page underneath
                stays put, no route navigation. See HeaderOverlay.tsx.
              */}
              <View className="flex-row items-center gap-1">
                <HeaderIconButton
                  onPress={() =>
                    setOpenOverlay((prev) => (prev === "search" ? null : "search"))
                  }
                  accessibilityLabel="Search"
                  active={openOverlay === "search"}
                >
                  {(c) => <IconHeaderSearch color={c} />}
                </HeaderIconButton>
                <HeaderIconButton
                  onPress={() =>
                    setOpenOverlay((prev) => (prev === "activity" ? null : "activity"))
                  }
                  accessibilityLabel="Open activity"
                  active={openOverlay === "activity"}
                  badgeCount={activityUnread}
                >
                  {(c) => <IconHeaderEnvelope color={c} />}
                </HeaderIconButton>
                <HeaderIconButton
                  onPress={() =>
                    setOpenOverlay((prev) => (prev === "settings" ? null : "settings"))
                  }
                  accessibilityLabel="Open settings"
                  active={openOverlay === "settings"}
                >
                  {(c) => <IconHeaderGear color={c} />}
                </HeaderIconButton>
                <Pressable
                  onPress={() =>
                    setOpenOverlay((prev) => (prev === "profile" ? null : "profile"))
                  }
                  accessibilityRole="button"
                  accessibilityLabel="Open profile"
                  style={({ hovered }) => [
                    {
                      width: 36,
                      height: 36,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: RADII.full,
                      marginLeft: 6,
                      // Soft amber ring on hover / when the profile overlay
                      // is open, so the bare-initials avatars still feel
                      // like an active control rather than dead chrome.
                      backgroundColor:
                        openOverlay === "profile"
                          ? STUDENT_THEME.sidebarItemActiveBg
                          : hovered && Platform.OS === "web"
                          ? STUDENT_THEME.surfaceDividerFaint
                          : "transparent",
                    },
                    Platform.OS === "web"
                      ? ({ transition: "background-color 0.15s ease" } as object)
                      : {},
                  ]}
                >
                  <Avatar name={avatarName} imageUrl={avatarUrl} size="sm" theme="dark" />
                </Pressable>
              </View>
            </View>
          </View>

          {!showSidebar && !hideSidebar ? (
            <View className="flex-row flex-wrap gap-2 border-b border-sand-dark/80 bg-sand px-3 py-2">
              {(
                [
                  ["/student", "Dashboard"],
                  ["/student/cases", "Cases"],
                  ["/student/teams", "Teams"],
                  ["/student/submissions", "Submissions"],
                  ["/student/profile", "Profile"],
                ] as const
              ).map(([href, label]) => (
                <Pressable
                  key={href}
                  onPress={() => router.push(href as Href)}
                  className="rounded-sm border border-sand-dark bg-white px-2.5 py-1.5"
                >
                  <Text className="text-[10px] font-bold uppercase tracking-wide text-text" style={{ fontFamily: FONTS.bodyBold }}>
                    {label}
                  </Text>
                </Pressable>
              ))}
            </View>
          ) : null}

          <View className="flex-1 bg-sand">
            {/* On-brand decorative backdrop — sage palm fronds + a faint amber
                wash. Sits absolute behind the page content via pointer-events
                none. Same motif as the marketing landing so the portal reads
                as part of the same brand world. */}
            <BrandBackdrop tone="sand" />
            <View style={{ flex: 1, zIndex: 1 }}>{children}</View>
          </View>
        </View>
      </View>

      {/*
        Persistent header overlays (Activity / Settings / Profile). These
        render on top of the shell as modals — the page underneath stays
        put. See HeaderOverlays.tsx + HeaderOverlay.tsx.
      */}
      <StudentHeaderOverlays
        open={openOverlay}
        onClose={closeOverlay}
        user={{
          fullName: resolvedIdentity.fullName,
          email: user?.email ?? null,
        }}
        avatarName={avatarName}
        avatarUrl={avatarUrl}
      />
    </SafeAreaView>
  );
}
